import { Button, Icon, ProposalSection, Tip } from '@vzmi/shp_shared_compo'
import _ from 'lodash'
import { Col, Row } from 'antd'
import { Form } from 'redux-form'
import moment from 'moment'
import { COMMA, idsParser, idsOnlyParser } from '@vzmi/component_ec_nodejs_util/dist/lib/parserUtil'
import { HistoryUtil } from '@vzmi/component_ec_nodejs_util/dist/lib'
import { EMPTY_VALUE } from '../../../constants/globalTypes'
import { DATE_FORMAT } from '../../../lib/MomentHelper'
import { pruneEmpty } from '../../../lib/FormHelper'
import DatePickerField from '../../Proposal/Common/ProposalForm/ProductInfo/DatePickerField'
import { InputField, MenuSelectField } from '../../Common/Form'
import { Field, formDecorator } from '../../Common/forms'
import CollapsePanel from '../../Proposal/UpdateListing/CollapsePanel/CollapsePanel'
import useOnInit from '../hook/useOnInit'
import styles from './SearchForm.css'

const FIELD = {
  ID: 'id',
  LISTING_ID: 'listingId',
  STATUS: 'status',
  TIME_TYPE: 'timeType',
  START_TS: 'startTs',
  END_TS: 'endTs'
}

const STATUS_PREFIX = {
  EXECUTE: 'executeStatus',
  REVERT: 'revertStatus'
}

const STATUS_SUFFIX = {
  IDLE: 'idle',
  FINISHED: 'finished'
}

const STATUS_OPTIONS = [
  { label: EMPTY_VALUE, value: EMPTY_VALUE },
  { label: '尚未開始', value: `${STATUS_PREFIX.EXECUTE}-${STATUS_SUFFIX.IDLE}` },
  { label: '已執行', value: `${STATUS_PREFIX.EXECUTE}-${STATUS_SUFFIX.FINISHED}` },
  { label: '待回檔', value: `${STATUS_PREFIX.REVERT}-${STATUS_SUFFIX.IDLE}` },
  { label: '已回檔', value: `${STATUS_PREFIX.REVERT}-${STATUS_SUFFIX.FINISHED}` }
]

const STATUS_OPTION_MAP = _.keyBy(STATUS_OPTIONS, 'value')

const TIME_TYPE = {
  EXECUTE_TS: 'executeTs',
  REVERT_TS: 'revertTs'
}

const TIME_TYPE_OPTIONS = [
  { label: '修改執行時間', value: TIME_TYPE.EXECUTE_TS },
  { label: '回檔執行時間', value: TIME_TYPE.REVERT_TS }
]

const INIT_VALUES = {
  [FIELD.ID]: '',
  [FIELD.LISTING_ID]: '',
  [FIELD.STATUS]: [STATUS_OPTIONS[0].value],
  [FIELD.TIME_TYPE]: [TIME_TYPE_OPTIONS[0].value],
  [FIELD.START_TS]: moment().add(-30, 'days').format(DATE_FORMAT.API_DATETIME_START),
  [FIELD.END_TS]: moment().add(60, 'days').format(DATE_FORMAT.API_DATETIME_END)
}

const syncValidator = (values = {}) => {
  const {
    [FIELD.START_TS]: startTs,
    [FIELD.END_TS]: endTs
  } = values
  const errors = {}
  const start = moment(startTs)
  const end = moment(endTs)
  const isValidTs = end.isAfter(start)
  if (startTs && endTs && !isValidTs) {
    _.set(errors, FIELD.START_TS, '開始時間需小於結束時間')
  }

  return errors
}

const disabledDate = field => dateMoment => {
  if (field === FIELD.START_TS) {
    const twoMonth = moment().add(60, 'days')
    return dateMoment.isAfter(twoMonth)
  }

  if (field === FIELD.END_TS) {
    const threeMonth = moment().add(90, 'days')
    return dateMoment.isAfter(threeMonth)
  }

  return false
}

const CONFIG = {
  form: 'QUERY_PAGE_SEARCH_FORM',
  validate: syncValidator,
  initialValues: INIT_VALUES
}

const customIdsParser = input => {
  return _.join(idsParser(input), COMMA)
}

export const getSearchParams = formValues => {
  const {
    [FIELD.ID]: id,
    [FIELD.LISTING_ID]: listingId,
    [FIELD.STATUS]: formStatusOption,
    [FIELD.TIME_TYPE]: timeTypeOption,
    [FIELD.START_TS]: startTs,
    [FIELD.END_TS]: endTs
  } = formValues
  if (!_.isEmpty(id)) {
    return { [FIELD.ID]: _.join(idsOnlyParser(id), COMMA) }
  }

  const searchParams = {}
  if (!_.isEmpty(listingId)) {
    _.set(searchParams, FIELD.LISTING_ID, _.join(idsOnlyParser(listingId), COMMA))
  }

  // 狀態為待回檔時 searchParams 會給 { executeStatus: 'finished', revertStatus: 'idle' }
  // 避免查到未執行提案
  const formStatus = _.get(formStatusOption, '0')
  if (formStatus !== STATUS_OPTIONS[0].value) {
    const isExecuteStatus = formStatus.startsWith(STATUS_PREFIX.EXECUTE)
    const statusKey = isExecuteStatus ? STATUS_PREFIX.EXECUTE : STATUS_PREFIX.REVERT
    const status = formStatus.replace(`${statusKey}-`, '')
    _.set(searchParams, statusKey, status)
  }
  if (formStatus === `${STATUS_PREFIX.REVERT}-${STATUS_SUFFIX.IDLE}`) {
    _.set(searchParams, STATUS_PREFIX.EXECUTE, STATUS_SUFFIX.FINISHED)
  }

  const timeType = _.get(timeTypeOption, '0')
  if (!_.isEmpty(startTs) && !_.isEmpty(endTs)) {
    const isISO8601 = [startTs, endTs].every(getIsISO8601)
    const formattedTs = isISO8601
      ? [startTs, endTs]
      : [
        moment(startTs).format(DATE_FORMAT.API_DATETIME_SEC_START),
        moment(endTs).format(DATE_FORMAT.API_DATETIME_SEC_END)
      ]
    const ts = _.join(formattedTs, '/')
    _.set(searchParams, timeType, ts)
  }

  return searchParams
}

const getDefaultId = id => {
  const parsedId = customIdsParser(id)
  if (id !== parsedId) {
    return ''
  }

  return id
}

const getIsISO8601 = dateString => {
  const isISO8601 = moment(dateString, DATE_FORMAT.API_TIMEZONE_DATETIME, true).isValid()
  return isISO8601
}

const getDefaultStatusProps = (statusProps) => {
  const field = FIELD.STATUS
  const { executeStatus, revertStatus } = statusProps
  const formattedExecuteStatus = `${STATUS_PREFIX.EXECUTE}-${executeStatus}`
  const isFormattedExecuteStatus = formattedExecuteStatus in STATUS_OPTION_MAP
  const formattedRrevertStatus = `${STATUS_PREFIX.REVERT}-${revertStatus}`
  const isFormattedRrevertStatus = formattedRrevertStatus in STATUS_OPTION_MAP

  if (
    executeStatus === STATUS_SUFFIX.FINISHED &&
    revertStatus === STATUS_SUFFIX.IDLE
  ) {
    return { [field]: [`${STATUS_PREFIX.REVERT}-${STATUS_SUFFIX.IDLE}`] }
  }

  if (isFormattedExecuteStatus) {
    return { [field]: [formattedExecuteStatus] }
  }

  if (isFormattedRrevertStatus) {
    return { [field]: [formattedRrevertStatus] }
  }

  return { [field]: INIT_VALUES[field] }
}

const getDefaultTsProps = tsProps => {
  const { executeTs = '', revertTs = '' } = tsProps
  const isExecuteTsExist = !_.isEmpty(executeTs)
  const executeStartEnd = _.split(executeTs, '/')
  const isValidExecuteTs = !_.some(executeStartEnd, ts => !getIsISO8601(ts))
  if (isExecuteTsExist && isValidExecuteTs) {
    const [startTs, endTs] = executeStartEnd
    return {
      [FIELD.TIME_TYPE]: [TIME_TYPE.EXECUTE_TS],
      [FIELD.START_TS]: moment(startTs, DATE_FORMAT.API_TIMEZONE_DATETIME).format(),
      [FIELD.END_TS]: moment(endTs, DATE_FORMAT.API_TIMEZONE_DATETIME).format()
    }
  }

  const isRevertTsExist = !_.isEmpty(revertTs)
  const revertStartEnd = _.split(revertTs, '/')
  const isValidRevertTs = !_.some(revertStartEnd, ts => !getIsISO8601(ts))
  if (isRevertTsExist && isValidRevertTs) {
    const [startTs, endTs] = revertStartEnd
    return {
      [FIELD.TIME_TYPE]: [TIME_TYPE.REVERT_TS],
      [FIELD.START_TS]: moment(startTs, DATE_FORMAT.API_TIMEZONE_DATETIME).format(),
      [FIELD.END_TS]: moment(endTs, DATE_FORMAT.API_TIMEZONE_DATETIME).format()
    }
  }

  return _.pick(INIT_VALUES, [FIELD.TIME_TYPE, FIELD.START_TS, FIELD.END_TS])
}

export const getDefaultFormValues = (urlSearchParams = {}) => {
  const {
    [FIELD.ID]: id = '',
    [FIELD.LISTING_ID]: listingId = '',
    [STATUS_PREFIX.EXECUTE]: executeStatus,
    [STATUS_PREFIX.REVERT]: revertStatus,
    [TIME_TYPE.EXECUTE_TS]: executeTs,
    [TIME_TYPE.REVERT_TS]: revertTs
  } = urlSearchParams
  const defaultId = getDefaultId(id)
  if (!_.isEmpty(defaultId)) {
    return {
      ...INIT_VALUES,
      [FIELD.ID]: defaultId
    }
  }

  const defaultFormValues = {
    [FIELD.ID]: defaultId,
    [FIELD.LISTING_ID]: getDefaultId(listingId),
    ...getDefaultStatusProps({ executeStatus, revertStatus }),
    ...getDefaultTsProps({ executeTs, revertTs })
  }
  return defaultFormValues
}

const customLabel = (options = [], value) => {
  const convertedValue = _.first(value)
  const label = _.get(
    _.find(options, { value: convertedValue }),
    'label',
    _.get(options, '0.label')
  )
  return label
}

const resetSearchParams = () => {
  const url = new URL(window.location)
  url.search = ''
  window.history.replaceState({}, '', url.toString())
}

const SearchForm = (props) => {
  const { handleSubmit, onSearch, initialize } = props

  const onSubmit = (formValues) => {
    const searchParams = getSearchParams(formValues)
    onSearch && onSearch(searchParams)
  }

  const onReset = () => {
    initialize(INIT_VALUES)
    resetSearchParams()
  }

  useOnInit(() => {
    const urlSearchParams = pruneEmpty(HistoryUtil.retrieveQueryParams(props))
    const defaultFormValues = getDefaultFormValues(urlSearchParams)
    initialize(defaultFormValues)
  })

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <ProposalSection>
        <CollapsePanel
          className={styles.appendBlock}
          panelHeader={(
            <Row
              align='middle'
              className={styles.panelHeader}
            >
              <Col span={4}>
                <span className={styles.iconHolder}>
                  <Icon
                    name='compose'
                    color='#e0e4e9'
                    size={15}
                  />
                </span>
                <strong className={styles.title}>
                  批次修改查詢
                </strong>
              </Col>
            </Row>
          )}
          defaultExpanded
        >
          <Row
            gutter={[16, 16]}
            className={styles.formArea}
            align='middle'
          >
            <Col span={4}>
              <Row justify='end'>
                批次編號
              </Row>
            </Col>
            <Col span={20}>
              <Field
                name={FIELD.ID}
                component={InputField}
                placeholder='可多個編號，以逗號分隔'
                parse={customIdsParser}
              />
              <Tip type='info'>
                以編號查詢時，其餘欄位條件會被忽略
              </Tip>
            </Col>
            <Col span={4}>
              <Row justify='end'>
                賣場編號
              </Row>
            </Col>
            <Col span={20}>
              <Field
                name={FIELD.LISTING_ID}
                component={InputField}
                placeholder='可多個編號，以逗號分隔'
                parse={customIdsParser}
              />
            </Col>
            <Col span={4}>
              <Row justify='end'>
                批次執行狀態
              </Row>
            </Col>
            <Col span={10}>
              <Field
                name={FIELD.STATUS}
                component={MenuSelectField}
                customLabel={customLabel}
                options={STATUS_OPTIONS}
                enableFilter={false}
                showOptionLabel
              />
            </Col>
            <Col span={10} />
            <Col span={4}>
              <Row justify='end'>
                <Field
                  name={FIELD.TIME_TYPE}
                  component={MenuSelectField}
                  customLabel={customLabel}
                  options={TIME_TYPE_OPTIONS}
                  enableFilter={false}
                  showOptionLabel
                />
              </Row>
            </Col>
            <Col span={5}>
              <Tip type='info'>
                開始時間
              </Tip>
              <Field
                name={FIELD.START_TS}
                component={DatePickerField}
                disabledDate={disabledDate(FIELD.START_TS)}
                absoluteError
                mustShowFormError
                showToday
              />
            </Col>
            <Col span={15}>
              <Tip type='info'>
                結束時間
              </Tip>
              <Field
                name={FIELD.END_TS}
                component={DatePickerField}
                disabledDate={disabledDate(FIELD.END_TS)}
                absoluteError
                mustShowFormError
                showToday
              />
            </Col>
            <Col span={24}>
              <Row
                justify='end'
                gutter={[16, 0]}
              >
                <Col>
                  <Button
                    size='large'
                    type='button'
                    onClick={onReset}
                  >
                    重置
                  </Button>
                </Col>
                <Col>
                  <Button
                    size='large'
                    type='submit'
                  >
                    查詢
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </CollapsePanel>
      </ProposalSection>
    </Form>
  )
}

export default formDecorator(CONFIG)(SearchForm)
